// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import { SaveWorkTimeLog } from "screens/Assessments/logic/GetWorkTimeLog";

const AddTimeLog = ({ open, handleClose, jobID, employees, mode = "add", log = null, onSuccess }) => {
  const theme = useTheme();

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && mode === "edit" && log) {
      setSelectedEmployee(log.employeeID);
      setCheckIn(log.checkIn);
      setCheckOut(log.checkOut);
  
    }
    if (!open && mode === "add") {
      // clear on add
      setSelectedEmployee("");
      setCheckIn(null);
      setCheckOut(null);
     
    }
  }, [open, mode, log]);

  const handleSave = async () => {
    if (!selectedEmployee || !checkIn || !checkOut) return;
  
    const payload = {
      workTimeLogID: mode === "edit" && log ? log.workTimeLogID : 0,
      jobID,
      employeeID: selectedEmployee,
      startDateTime: dayjs(checkIn).format("YYYY-MM-DDTHH:mm:ss"),
      endDateTime: dayjs(checkOut).format("YYYY-MM-DDTHH:mm:ss"),
      duration: 0,
      breakMinutes: 0,
      laborType: "Regular",
      status: "Submitted",
      notes: ""
    };
  
    const result = await SaveWorkTimeLog(payload);
    if (result) {
      onSuccess?.();
      handleClose();
    }
  };
  

  const invalidRange = checkIn && checkOut && (checkIn.isAfter(checkOut) || !checkIn.isSame(checkOut, "day"));

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {mode === "edit" ? "Edit Time Log" : "Add Time Log"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 4 }}>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <CustomTextField
              select
              fullWidth
              label="Employee"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              {employees.map((employee) => (
                <MenuItem key={employee.employeeID} value={employee.employeeID}>
                  {`${employee.firstName} ${employee.lastName}`}
                </MenuItem>
              ))}
            </CustomTextField>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Check In"
                value={checkIn}
                onChange={setCheckIn}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    label: "Check In",
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Check Out"
                value={checkOut}
                onChange={setCheckOut}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    label: "Check Out",
                    error: invalidRange,
                    helperText: invalidRange ? "Check out must be after check in on the same day" : "",
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* <Grid item xs={12}>
            <CustomTextField
              select
              fullWidth
              label="Logged By"
              value={loggedBy}
              onChange={(e) => setLoggedBy(e.target.value)}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Crew Leader">Crew Leader</MenuItem>
            </CustomTextField>
          </Grid> */}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 5 }}>
        <Button variant="outlined" color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="main"
          onClick={handleSave}
          disabled={!selectedEmployee || !checkIn || !checkOut || invalidRange}
        >
          {mode === "edit" ? "Save Changes" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTimeLog; 